/**
 * Transactions advanced-filter document — a versioned value the web filter
 * panel (`TransactionFilterPanel`) edits and evaluates CLIENT-SIDE over the
 * complete ledger transaction index. One schema + ONE evaluator, both in
 * @releve/shared (the expenses / AP / AR / chart-of-accounts template),
 * shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyTransactionFilterDocument` —
 *    never build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session.
 *
 * ── THIS WAS A MOVE, NOT AN EXPANSION ───────────────────────────────────────
 * v1's vocabulary is EXACTLY the columns the transactions grids used to mark
 * `filterable: true` (transactionGridColumns.tsx): the eight index columns plus
 * the account register's Cleared column. BOTH grids now share this document —
 * the org transactions grid (`TransactionsEditableTable`) and the per-account
 * register (`AccountRegisterEditableTable`); `cleared_state` is the one
 * register-only field (index rows carry none, so it never matches there).
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both. Neither transactions grid has a filterable column.
 *
 * v1 fields are `transaction.<field>` over the MAPPED grid rows
 * (`TransactionGridRow`), so evaluation runs AFTER the mapper and BEFORE the
 * grid shell (`hasUpstreamFilters`).
 *
 * ── THE SEEDED DEFAULT: VOIDED TRANSACTIONS ARE HIDDEN ─────────────────────
 * A voided original and its reversal net to zero; the browse list hides both
 * by default. `void_status` is a CLOSED vocabulary — `'none'` for an ordinary
 * journal (the row's `void_status` is null), `'reversed'` for a voided
 * original, `'reversal'` for the journal that voids it — so "not voided" is a
 * real token, the chart-of-accounts `account.scope in ['standard']` precedent,
 * never a restyled "(empty)". `DEFAULT_TRANSACTION_FILTER_DOCUMENT` carries
 * `void_status in ['none']`. The grid's "N voided transactions are hidden ·
 * Show voided" notice REMOVES that condition (`withoutVoidStatusCondition`).
 * Accepted consequences, all deliberate and shared with the CoA build: the
 * filter badge reads "1" on first load, Clear resets to
 * `EMPTY_TRANSACTION_FILTER_DOCUMENT` (every row, voided included) and NOT
 * back to the default, and the grid's `hasUpstreamFilters` is REMOVAL-based.
 *
 * ── THE BUSINESS FIELD CARRIES RAW NAMES ────────────────────────────────────
 * The grid row has no business id — `business_name` is the header business
 * name with the linked expense's payee fallback, so the family filters by
 * NAME (as the column did). A name has no length bound in the database while
 * a condition value is capped at 200 characters and 50 values
 * (`filterDocumentPrimitives.ts`). The panel never parses its ad-hoc
 * document, so filtering still works — but a document built from such a name
 * will NOT validate against the schema (the expense payee caveat).
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1) — the eight ex-`filterable` index columns
// ---------------------------------------------------------------------------
export const TRANSACTION_FILTER_STRING_FIELDS = [
    "transaction.transaction_type",
    "transaction.wkr_summary",
    "transaction.void_status",
    "transaction.project_id",
    "transaction.business_name",
    "transaction.fund_id",
    "transaction.cleared_state",
];
export const TRANSACTION_FILTER_DATE_FIELDS = [
    "transaction.transaction_date",
    "transaction.record_created_at",
];
/**
 * The void-status family's closed vocabulary, in panel order. `'none'` is the
 * token an ordinary (never-voided) journal evaluates as.
 */
export const TRANSACTION_VOID_STATUS_TOKENS = ["none", "reversed", "reversal"];
/**
 * The register's Cleared family, in panel order — mirrors web's `ClearedState`
 * (transactionGridMapper.ts; a type-level assertion in the mapper test keeps
 * the two from drifting). `void` is a mutually-uncleared void pair, a fact
 * about two rows the register mapper resolves list-wide.
 */
export const TRANSACTION_CLEARED_STATE_TOKENS = ["cleared", "uncleared", "void"];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(TRANSACTION_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(TRANSACTION_FILTER_DATE_FIELDS);
export const transactionFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const transactionFilterDocumentSchema = makeFilterDocumentSchema(transactionFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_TRANSACTION_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/** The grid's initial document — voided transactions hidden (see the docblock). */
export const DEFAULT_TRANSACTION_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [{ field: "transaction.void_status", op: "in", values: ["none"] }],
};
/**
 * Strip ONLY the void-status condition, leaving every other family untouched
 * — "Show voided" widens one family, it is not a reset. Lives here (not in the
 * grid, unlike CoA's `withoutScopeCondition`) so the shared test pins it.
 */
export function withoutVoidStatusCondition(doc) {
    return {
        ...doc,
        conditions: doc.conditions.filter((c) => c.field !== "transaction.void_status"),
    };
}
/**
 * True while the document carries the SEEDED void-status condition exactly
 * (`in ['none']`) — the one state in which the grid announces hidden voided
 * transactions. Compared against `DEFAULT_TRANSACTION_FILTER_DOCUMENT`, so
 * the seeded spelling lives in one place.
 */
export function hidesVoidedTransactions(doc) {
    const seeded = DEFAULT_TRANSACTION_FILTER_DOCUMENT.conditions[0];
    const condition = doc.conditions.find((c) => c.field === "transaction.void_status");
    if (!condition || condition.op !== "in" || seeded.op !== "in")
        return false;
    return (condition.include_empty === undefined &&
        condition.values?.length === seeded.values?.length &&
        (condition.values ?? []).every((v, i) => v === seeded.values?.[i]));
}
/**
 * Widen the void-status family just enough to show a journal with `token`
 * (a deep link or related-transaction link to a hidden voided row):
 *  - from the seeded default → drop the condition, so BOTH halves of the pair
 *    show (what the grid's "Show voided" does);
 *  - from the user's own `in` selection → ADD the token, keeping their
 *    narrowing (Reversed only + a link to its reversal → Reversed + Reversal);
 *  - anything else → drop the condition.
 * The other families are never touched.
 */
export function revealVoidStatus(doc, token) {
    if (hidesVoidedTransactions(doc))
        return withoutVoidStatusCondition(doc);
    return {
        ...doc,
        conditions: doc.conditions.flatMap((c) => {
            if (c.field !== "transaction.void_status")
                return [c];
            if (c.op !== "in")
                return [];
            const values = c.values ?? [];
            return [values.includes(token) ? c : { ...c, values: [...values, token] }];
        }),
    };
}
/** The void-status token a row evaluates as (null → `'none'`). */
export function voidStatusTokenOf(row) {
    return row.void_status ?? "none";
}
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary: a new vocabulary entry is a `TS2366` until it is handled here.
 */
function stringValue(row, field) {
    switch (field) {
        case "transaction.transaction_type":
            return row.transaction_type ?? "";
        case "transaction.wkr_summary":
            return row.wkr_summary ?? "";
        case "transaction.void_status":
            return voidStatusTokenOf(row);
        case "transaction.project_id":
            return row.project_id ?? "";
        case "transaction.business_name":
            return row.business_name ?? "";
        case "transaction.fund_id":
            return row.fund_id ?? "";
        case "transaction.cleared_state":
            return row.cleared_state ?? "";
    }
}
/** The LOCAL calendar day (`yyyy-MM-dd`) to compare, as a string. */
function dateValue(row, field) {
    switch (field) {
        case "transaction.transaction_date":
            return row.transaction_date;
        case "transaction.record_created_at":
            return toLocalCalendarDay(row.record_created_at);
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped transaction rows (AND of all conditions —
 * v1 `match` is 'all' only). An empty document returns the input array
 * unchanged (identity — callers can rely on reference equality).
 */
export function applyTransactionFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveTransactionConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=transactionFilters.js.map